import { type FormEvent, useMemo, useState } from 'react';
import type { DiaCalendario, ResultadoGeracao } from '@pcp/contracts';
import {
  isConflict,
  isOk,
  useAtualizarCalendario,
  useCalendario,
  useGerarCalendario,
  useMaquinas,
} from '../../api/cadastros';
import { ErrorState, LoadingBlock } from '../../components/states';
import { formatDate } from '../../format';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.calendar;
const WEEKDAY = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/** `2026-10` → primeiro e último dia, e a lista de datas do mês. */
function monthDays(ym: string) {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  const days = Array.from({ length: last }, (_, i) => {
    const iso = `${y}-${pad(m)}-${pad(i + 1)}`;
    return { iso, day: i + 1, dow: new Date(Date.UTC(y, m - 1, i + 1)).getUTCDay() };
  });
  return { de: days[0]!.iso, ate: days[days.length - 1]!.iso, days };
}

const initialMonth = () => '2026-10';

export function CalendarioTab({ canEdit }: { canEdit: boolean }) {
  const [month, setMonth] = useState(initialMonth);
  const [machineFilter, setMachineFilter] = useState('');
  const { de, ate, days } = useMemo(() => monthDays(month), [month]);
  const machines = useMaquinas();
  const query = useCalendario({ de, ate, ...(machineFilter ? { maquina: machineFilter } : {}) });
  const update = useAtualizarCalendario();
  const generate = useGerarCalendario();

  const [selected, setSelected] = useState<{ maquina: string; data: string } | null>(null);
  const [edit, setEdit] = useState<Partial<DiaCalendario>>({});
  const [dayMsg, setDayMsg] = useState<{ kind: 'conflict' | 'error' | 'ok'; text: string } | null>(null);

  // geração
  const [genFrom, setGenFrom] = useState(de);
  const [genTo, setGenTo] = useState(ate);
  const [genMachines, setGenMachines] = useState<string[]>([]);
  const [genResult, setGenResult] = useState<ResultadoGeracao[] | null>(null);

  // parada em várias máquinas
  const [bulkDate, setBulkDate] = useState('');
  const [bulkHours, setBulkHours] = useState(8);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkMachines, setBulkMachines] = useState<string[]>([]);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  const byKey = useMemo(() => {
    const m = new Map<string, DiaCalendario>();
    for (const d of query.data ?? []) m.set(`${d.maquina}|${d.data}`, d);
    return m;
  }, [query.data]);
  const shownMachines = (machines.data ?? []).filter((m) => !machineFilter || m.maquina === machineFilter);
  const current = selected ? byKey.get(`${selected.maquina}|${selected.data}`) : undefined;

  const pick = (maquina: string, data: string) => {
    setSelected({ maquina, data });
    setEdit({});
    setDayMsg(null);
  };
  const val = <K extends keyof DiaCalendario>(k: K): DiaCalendario[K] | undefined =>
    (k in edit ? edit[k] : current?.[k]) as DiaCalendario[K] | undefined;

  const saveDay = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || !current || !Object.keys(edit).length) return;
    const [r] = await update.mutateAsync([
      { ...edit, maquina: selected.maquina, data: selected.data, version: current.version },
    ]);
    if (!r) return;
    if (isOk(r)) {
      setEdit({});
      setDayMsg({ kind: 'ok', text: t.cadastros.saved });
    } else if (isConflict(r)) {
      setDayMsg({ kind: 'conflict', text: `${t.cadastros.conflictTitle} ${t.cadastros.conflictText}` });
    } else {
      setDayMsg({ kind: 'error', text: r.errordescription });
    }
  };

  const runGenerate = async (e: FormEvent) => {
    e.preventDefault();
    const res = await generate.mutateAsync(
      genMachines.map((maquina) => ({ maquina, 'dt-ini': genFrom, 'dt-fim': genTo })),
    );
    setGenResult(res);
  };

  const runBulk = async (e: FormEvent) => {
    e.preventDefault();
    const dias = bulkMachines.flatMap((maquina) => {
      const d = byKey.get(`${maquina}|${bulkDate}`);
      return d
        ? [
            {
              maquina,
              data: bulkDate,
              'horas-parada': bulkHours,
              ...(bulkReason ? { 'motivo-parada': bulkReason } : {}),
              version: d.version,
            },
          ]
        : [];
    });
    if (!dias.length) {
      setBulkMsg(c.notGenerated);
      return;
    }
    const res = await update.mutateAsync(dias);
    const ok = res.filter(isOk).length;
    setBulkMsg(c.applied(ok, res.length - ok));
  };

  const toggle = (list: string[], set: (v: string[]) => void, id: string) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <section className="panel" aria-labelledby="tab-calendar">
      <h2 id="tab-calendar">{t.cadastros.tabs.calendar}</h2>
      <p className="hint">{c.hint}</p>

      <div className="row">
        <label className="inline-label" htmlFor="cal-month">
          {c.month}
        </label>
        <input
          id="cal-month"
          type="month"
          value={month}
          onChange={(e) => e.target.value && setMonth(e.target.value)}
        />
        <label className="inline-label" htmlFor="cal-machine">
          {c.machine}
        </label>
        <select
          id="cal-machine"
          className="select"
          value={machineFilter}
          onChange={(e) => setMachineFilter(e.target.value)}
        >
          <option value="">{t.cadastros.all}</option>
          {(machines.data ?? []).map((m) => (
            <option key={m.maquina} value={m.maquina}>
              {m.descricao}
            </option>
          ))}
        </select>
      </div>

      {query.isLoading || machines.isLoading ? <LoadingBlock /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}

      {query.data ? (
        <div className="scroll">
          <table className="cal-grid" aria-label={`${t.cadastros.tabs.calendar} ${month}`}>
            <thead>
              <tr>
                <th>{c.machine}</th>
                {days.map((d) => (
                  <th key={d.iso} className={`day${d.dow === 0 || d.dow === 6 ? ' weekend' : ''}`}>
                    {d.day}
                    <small>{WEEKDAY[d.dow]}</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownMachines.map((m) => (
                <tr key={m.maquina}>
                  <td>
                    <strong>{m.descricao}</strong>
                  </td>
                  {days.map((d) => {
                    const day = byKey.get(`${m.maquina}|${d.iso}`);
                    const isSel = selected?.maquina === m.maquina && selected.data === d.iso;
                    const cls = !day ? 'missing' : day['horas-parada'] > 0 ? 'stop' : 'normal';
                    return (
                      <td key={d.iso} className={`c ${cls}${isSel ? ' sel' : ''}`}>
                        <button
                          type="button"
                          aria-label={`${m.descricao} ${formatDate(d.iso)}${day ? `, parada ${num(day['horas-parada'], 2)} h` : ', ' + c.legendMissing}`}
                          aria-pressed={isSel}
                          onClick={() => pick(m.maquina, d.iso)}
                        >
                          {day ? (day['horas-parada'] > 0 ? num(day['horas-parada'], 1) : '') : ''}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="legend">
        <span>
          <i className="swatch normal" />
          {c.legendNormal}
        </span>
        <span>
          <i className="swatch stop" />
          {c.legendStop}
        </span>
        <span>
          <i className="swatch missing" />
          {c.legendMissing}
        </span>
      </div>

      {selected ? (
        <form
          className="new-row"
          onSubmit={(e) => void saveDay(e)}
          aria-label={`${c.day} ${formatDate(selected.data)}`}
        >
          <strong>
            {machines.data?.find((m) => m.maquina === selected.maquina)?.descricao ?? selected.maquina} ·{' '}
            {formatDate(selected.data)}
          </strong>
          {!current ? (
            <p className="hint">{c.notGenerated}</p>
          ) : (
            <>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="day-turnos">{c.shifts}</label>
                  <input
                    id="day-turnos"
                    type="number"
                    min={0}
                    max={4}
                    disabled={!canEdit}
                    value={val('qtd-turnos') ?? ''}
                    onChange={(e) => setEdit((d) => ({ ...d, 'qtd-turnos': Number(e.target.value) }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="day-disp">{c.availableHours}</label>
                  <input
                    id="day-disp"
                    type="number"
                    step="0.01"
                    min={0}
                    max={24}
                    disabled={!canEdit}
                    value={val('horas-disponiveis') ?? ''}
                    onChange={(e) => setEdit((d) => ({ ...d, 'horas-disponiveis': Number(e.target.value) }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="day-parada">{c.stopHours}</label>
                  <input
                    id="day-parada"
                    type="number"
                    step="0.5"
                    min={0}
                    max={24}
                    disabled={!canEdit}
                    value={val('horas-parada') ?? ''}
                    onChange={(e) => setEdit((d) => ({ ...d, 'horas-parada': Number(e.target.value) }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="day-motivo">{c.stopReason}</label>
                  <input
                    id="day-motivo"
                    disabled={!canEdit}
                    value={val('motivo-parada') ?? ''}
                    onChange={(e) => setEdit((d) => ({ ...d, 'motivo-parada': e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="day-obs">{c.note}</label>
                  <input
                    id="day-obs"
                    disabled={!canEdit}
                    value={val('observacao') ?? ''}
                    onChange={(e) => setEdit((d) => ({ ...d, observacao: e.target.value }))}
                  />
                </div>
              </div>
              {canEdit ? (
                <div className="row">
                  <button
                    type="submit"
                    className="btn"
                    disabled={!Object.keys(edit).length || update.isPending}
                  >
                    {t.cadastros.save}
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setSelected(null)}>
                    {t.cadastros.cancel}
                  </button>
                </div>
              ) : null}
              {dayMsg?.kind === 'ok' ? (
                <span className="chip ok" role="status">
                  {dayMsg.text}
                </span>
              ) : null}
              {dayMsg && dayMsg.kind !== 'ok' ? (
                <div className="row-conflict" role="alert">
                  <span>{dayMsg.text}</span>
                  {dayMsg.kind === 'conflict' ? (
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => {
                        setEdit({});
                        setDayMsg(null);
                        void query.refetch();
                      }}
                    >
                      {t.cadastros.reload}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </form>
      ) : null}

      {canEdit ? (
        <div className="grid2">
          <form className="new-row" onSubmit={(e) => void runGenerate(e)} aria-label={c.generateTitle}>
            <strong>{c.generateTitle}</strong>
            <p className="hint">{c.generateHint}</p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="gen-de">{c.from}</label>
                <input id="gen-de" type="date" value={genFrom} onChange={(e) => setGenFrom(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="gen-ate">{c.to}</label>
                <input id="gen-ate" type="date" value={genTo} onChange={(e) => setGenTo(e.target.value)} />
              </div>
            </div>
            <fieldset className="roles">
              <legend className="hint">{c.machines}</legend>
              {(machines.data ?? []).map((m) => (
                <label key={m.maquina} className="check">
                  <input
                    type="checkbox"
                    checked={genMachines.includes(m.maquina)}
                    onChange={() => toggle(genMachines, setGenMachines, m.maquina)}
                  />
                  <span>{m.descricao}</span>
                </label>
              ))}
            </fieldset>
            <div>
              <button
                type="submit"
                className="btn"
                disabled={!genMachines.length || !genFrom || !genTo || generate.isPending}
              >
                {c.run}
              </button>
            </div>
            {genResult ? (
              <ul className="gen-result" aria-label={c.generate}>
                {genResult.map((r) => (
                  <li key={`${r.maquina}-${r['dt-ini']}`} className={r.situacao === 'OK' ? 'ok' : 'bad'}>
                    <strong>{r.maquina}</strong>:{' '}
                    {r.situacao === 'OK'
                      ? c.generated(r['dias-gerados'], r['dias-existentes'])
                      : r.errordescription}
                  </li>
                ))}
              </ul>
            ) : null}
          </form>

          <form className="new-row" onSubmit={(e) => void runBulk(e)} aria-label={c.bulkTitle}>
            <strong>{c.bulkTitle}</strong>
            <p className="hint">{c.bulkHint}</p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="bulk-data">{c.date}</label>
                <input
                  id="bulk-data"
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="bulk-horas">{c.hours}</label>
                <input
                  id="bulk-horas"
                  type="number"
                  min={0}
                  max={24}
                  step="0.5"
                  value={bulkHours}
                  onChange={(e) => setBulkHours(Number(e.target.value))}
                />
              </div>
              <div className="field">
                <label htmlFor="bulk-motivo">{c.reason}</label>
                <input id="bulk-motivo" value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} />
              </div>
            </div>
            <fieldset className="roles">
              <legend className="hint">{c.machines}</legend>
              {(machines.data ?? []).map((m) => (
                <label key={m.maquina} className="check">
                  <input
                    type="checkbox"
                    checked={bulkMachines.includes(m.maquina)}
                    onChange={() => toggle(bulkMachines, setBulkMachines, m.maquina)}
                  />
                  <span>{m.descricao}</span>
                </label>
              ))}
            </fieldset>
            <div>
              <button
                type="submit"
                className="btn"
                disabled={!bulkMachines.length || !bulkDate || update.isPending}
              >
                {c.apply}
              </button>
            </div>
            {bulkMsg ? (
              <span className="chip info" role="status">
                {bulkMsg}
              </span>
            ) : null}
          </form>
        </div>
      ) : null}
    </section>
  );
}
